import { Globe, Monitor, Moon, Sun } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { APP_LANGUAGES, type AppLanguage } from '@/services/i18n/languages'

type ThemeChoice = 'light' | 'dark' | 'system'

// On the phone the joined segments become separate 36 px chips, so a group can wrap instead of
// scrolling sideways (the language group does not fit one row at 375 px).
const PHONE_CHIP = 'max-md:h-9 max-md:rounded-lg! max-md:border-l!'

export interface AppearanceSectionProps {
  theme: ThemeChoice
  language: AppLanguage
  railLabels: boolean
  onThemeChange: (theme: ThemeChoice) => void
  onLanguageChange: (language: AppLanguage) => void
  onRailLabelsChange: (value: boolean) => void
}

export function AppearanceSection({
  theme,
  language,
  railLabels,
  onThemeChange,
  onLanguageChange,
  onRailLabelsChange,
}: AppearanceSectionProps) {
  const { t } = useTranslation()

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.appearance.title')}</CardTitle>
        <CardDescription>{t('settings.appearance.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>{t('settings.appearance.theme')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('settings.appearance.themeDescription')}
          </p>
          <ToggleGroup
            type="single"
            value={theme}
            onValueChange={value => value && onThemeChange(value as ThemeChoice)}
            variant="outline"
            size="sm"
            className="justify-start pt-1 max-md:flex-wrap max-md:gap-2"
          >
            <ToggleGroupItem value="light" aria-label="Light mode" className={PHONE_CHIP}>
              <Sun className="h-3.5 w-3.5" />
              {t('settings.appearance.light')}
            </ToggleGroupItem>
            <ToggleGroupItem value="dark" aria-label="Dark mode" className={PHONE_CHIP}>
              <Moon className="h-3.5 w-3.5" />
              {t('settings.appearance.dark')}
            </ToggleGroupItem>
            <ToggleGroupItem value="system" aria-label="System theme" className={PHONE_CHIP}>
              <Monitor className="h-3.5 w-3.5" />
              {t('settings.appearance.system')}
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="space-y-2">
          <Label>{t('settings.appearance.language')}</Label>
          <p className="text-sm text-muted-foreground">
            {t('settings.appearance.languageDescription')}
          </p>
          <ToggleGroup
            type="single"
            value={language}
            onValueChange={value => value && onLanguageChange(value as AppLanguage)}
            variant="outline"
            size="sm"
            className="justify-start pt-1 max-md:flex-wrap max-md:gap-2"
          >
            {APP_LANGUAGES.map(({ code, label }) => (
              <ToggleGroupItem key={code} value={code} aria-label={label} className={PHONE_CHIP}>
                <Globe className="h-3.5 w-3.5" />
                {label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <Label htmlFor="appearance-rail-labels">{t('settings.appearance.navLabels')}</Label>
              <p className="text-sm text-muted-foreground">
                {t('settings.appearance.navLabelsDescription')}
              </p>
            </div>
            <Switch
              id="appearance-rail-labels"
              checked={railLabels}
              onCheckedChange={onRailLabelsChange}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
